import { cn } from "@/lib/utils";
import axiosInstance from "@services/index.services";
import {
  InfiniteData,
  QueryObserverResult,
  RefetchOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { getFileIcon, showToast } from "@utils/helper/index.helper";
import { Copy, X, RefreshCw } from "lucide-react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

// Type for uploaded file with progress, error, and uploading state
type UploadedFile = {
  file: File;
  progress: number;
  error?: string;
  id?: string | number;
  image?: string; // URL for preview
  uploading?: boolean;
  name?: string; // Custom name for the file
};

const FileItem = ({
  uploadedFile,
  multiple,
  allowCopy = false,
  onRemove,
  onRetry,
  onNameChange,
}: {
  uploadedFile: UploadedFile;
  multiple: boolean;
  allowCopy?: boolean;
  onRemove?: (fileName: string) => void;
  onRetry?: (file: File) => void;
  onNameChange?: (fileName: string, newName: string) => void;
}) => {
  const { file, progress, error, uploading, name } = uploadedFile;
  const Icon = getFileIcon(file.type);
  const fileSize = file.size / 1024 / 1024;

  return (
    <div className="relative flex flex-col space-y-2 p-2 border rounded-md bg-white shadow-sm">
      {onRemove && (
        <X
          className="text-red-500 absolute top-1 right-1 cursor-pointer hover:text-red-700"
          size={14}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(file.name);
          }}
        />
      )}
      <div className="flex items-center space-x-2">
        <Icon className="w-5 h-5 text-gray-600" />
        <div
          className={
            multiple
              ? "text-[13px] w-fit max-w-[100px] truncate"
              : "text-[13px] w-fit max-w-[200px] truncate"
          }
        >
          {file.name}
          <div className="block max-w-[100px] truncate w-fit text-xs text-gray-500">
            {fileSize.toFixed(2)}MB
          </div>

        {progress !== undefined && uploading && (
          <div className="w-full bg-gray-200 h-2 rounded mt-1">
            <div
              className={cn(
                "h-2 rounded",
                error ? "bg-red-500" : "bg-green-500",
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {error && (
          <div className="text-xs text-red-600 mt-1 truncate">{error}</div>
        )}
      </div>
      </div>
      <input
        type="text"
        placeholder="Enter icon name"
        value={name || ""}
        onChange={(e) => onNameChange?.(file.name, e.target.value)}
        className="w-full text-xs p-1 border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
        disabled={uploading}
      />

      <div className="flex items-center justify-end space-x-2">
        {error && onRetry && (
          <RefreshCw
            size={14}
            className="text-blue-500 cursor-pointer hover:text-blue-700"
            onClick={() => onRetry(file)}
          />
        )}

        {allowCopy && (
          <span
            onClick={() => {
              window.navigator.clipboard.writeText(file.name);
              showToast({
                description: "Copied To Clipboard",
                title: "Success",
                type: "success",
              });
            }}
            className="size-6 flex items-center justify-center bg-[#1daa7e] rounded-full"
          >
            <Copy size={12} className="text-white" />
          </span>
        )}
      </div>
    </div>
  );
};

type FileUploadProps = {
  multiple?: boolean;
  onChange?: (files: (number | string)[]) => void; // Only send IDs
  value: (number | string)[];
  allowCopy?: boolean;
  endpoint?: string;
  additionalBody?: any;
  manualUpload?: boolean;
  uploadToCloud?: boolean;
};

function FileUpload({
  multiple = true,
  onChange,
  additionalBody,
  value,
  allowCopy,
  endpoint = "/custom-gallery/",
  manualUpload = false,
}: FileUploadProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const handleNameChange = (fileName: string, newName: string) => {
    setUploadedFiles((prev) =>
      prev.map((uf) =>
        uf.file.name === fileName ? { ...uf, name: newName } : uf
      )
    );
  };
  const queryClient = useQueryClient();
  const uploadFile = async (files: File[]) => {
    // Initialize uploaded files with uploading state
    const newUploadedFiles: UploadedFile[] = files.map((file: File) => ({
      file,
      progress: 0,
      error: undefined,
      uploading: true,
    }));

    setUploadedFiles((prev) => [...prev, ...newUploadedFiles]);

    // Upload each file individually
    // const uploadPromises = files.map(async (file, fileIndex) => {
    try {
      const formData = new FormData();
      if (multiple) {
        files.forEach((file, fileIndex) => {
          formData.append(`image${fileIndex}`, file); // Single file per request

          // Add additional body data if provided
          if (additionalBody && typeof additionalBody === "object") {
            Object.entries(additionalBody).forEach(([key, value]) => {
              formData.append(key + fileIndex, String(value));
            });
          }
        });
      } else {
        formData.append(`image`, files[0]);
      }

      console.log("Uploading to endpoint:", files);

      const response = await axiosInstance.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / (event.total ?? 1));
          setUploadedFiles((prev) =>
            prev.map((uf, index) =>
              uf.file.name === files[index].name && uf.uploading
                ? { ...uf, progress: percent > 99 ? 99 : percent }
                : uf,
            ),
          );
        },
      });
      queryClient.invalidateQueries({ queryKey: ["Custom Galery"] });
      // Update the specific file with success data
      setUploadedFiles((prev) =>
        prev.map((uf, index) =>
          uf.file.name === files[index].name && uf.uploading
            ? {
                ...uf,
                progress: 100,
                uploading: false,
                id: response.data.id,
                image: response.data.image || response.data.url,
              }
            : uf,
        ),
      );

      showToast({
        title: "Upload Complete",
        description: `${files[0].name} uploaded successfully`,
        type: "success",
      });

      return response.data.id; // Return the ID for onChange callback
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err.message || "Upload failed";

      queryClient.invalidateQueries({ queryKey: ["Custom Galery"] });
      setUploadedFiles((prev) =>
        prev.map((uf) =>
          uf.file.name === files[0].name && uf.uploading
            ? { ...uf, error: msg, progress: 100, uploading: false }
            : uf,
        ),
      );

      showToast({
        title: "Upload Failed",
        description: `${files[0].name}: ${msg}`,
        type: "error",
      });

      return null; // Return null for failed uploads
    }
    // });

    // Wait for all uploads to complete
    // const results = await Promise.all(uploadPromises);

  };

  // Alternative approach: Send all files in a single request (if your backend supports it)
  const uploadWithNames = async () => {
    const filesToUpload = uploadedFiles.filter((uf) => !uf.id && !uf.uploading);
    if (filesToUpload.length === 0) return;

    // Mark as uploading
    setUploadedFiles((prev) =>
      prev.map((uf) =>
        filesToUpload.some((f) => f.file.name === uf.file.name)
          ? { ...uf, uploading: true, progress: 0 }
          : uf,
      ),
    );

    try {
      const formData = new FormData();
      filesToUpload.forEach((uf, index) => {
        formData.append(`image${index}`, uf.file);
        formData.append(`name${index}`, uf.name || uf.file.name);
        if (additionalBody && typeof additionalBody === "object") {
          Object.entries(additionalBody).forEach(([key, val]) => {
            formData.append(`${key}${index}`, String(val));
          });
        }
      });

      const response = await axiosInstance.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / (event.total ?? 1));
          setUploadedFiles((prev) =>
            prev.map((uf) =>
              uf.uploading ? { ...uf, progress: percent > 99 ? 99 : percent } : uf,
            ),
          );
        },
      });

      const responseData = Array.isArray(response.data)
        ? response.data
        : [response.data];

      setUploadedFiles((prev) =>
        prev.map((uf) => {
          const match = responseData.find(
            (res: any) =>
              res.name === uf.name ||
              res.image?.includes(uf.file.name) ||
              res.url?.includes(uf.file.name),
          );
          if (match) {
            return {
              ...uf,
              progress: 100,
              uploading: false,
              id: match.id,
              image: match.image || match.url,
            };
          }
          return uf;
        }),
      );

      const uploadedIds = responseData.map((item: any) => item.id);
      const currentValue = Array.isArray(value) ? value : [];
      onChange?.([...currentValue, ...uploadedIds]);

      showToast({
        title: "Upload Complete",
        description: `${responseData.length} file(s) uploaded successfully`,
        type: "success",
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || "Upload failed";
      setUploadedFiles((prev) =>
        prev.map((uf) =>
          uf.uploading ? { ...uf, error: msg, progress: 100, uploading: false } : uf,
        ),
      );
      showToast({
        title: "Upload Failed",
        description: msg,
        type: "error",
      });
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const unique = acceptedFiles.filter(
        (f) => !uploadedFiles.some((existing) => existing.file.name === f.name),
      );

      if (unique.length > 0) {
        if (manualUpload) {
          // Just stage the files
          const newStagedFiles: UploadedFile[] = unique.map((file: File) => ({
            file,
            progress: 0,
            error: undefined,
            uploading: false,
            name: "",
          }));
          setUploadedFiles((prev) => [...prev, ...newStagedFiles]);
        } else {
          uploadFile(unique);
        }
      }
    },
    [uploadedFiles, manualUpload],
  );

  const handleRemoveUploadedFile = (fileName: string) => {
    setUploadedFiles((prev) => {
      const fileToRemove = prev.find((uf) => uf.file.name === fileName);
      const updated = prev.filter((uf) => uf.file.name !== fileName);

      if (fileToRemove?.id) {
        const updatedIds = value.filter((id) => id !== fileToRemove.id);
        onChange?.(updatedIds);
      }

      return updated;
    });
  };

  const handleRetry = (file: File) => {
    console.log("retrying file:", file);
    setUploadedFiles((prev) => prev.filter((uf) => uf.file.name !== file.name));
    uploadFile([file]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
  });

  return (
    <>
      <div
        {...getRootProps()}
        className="border-dashed text-[#0d4936] bg-[#e8f6f1] border-2 rounded-2xl min-h-[200px] w-full flex justify-between items-center cursor-pointer"
      >
        <input {...getInputProps()} />
        <div className="flex justify-center items-center w-full text-sm">
          {isDragActive ? (
            <p className="text-center">Drop the files here ...</p>
          ) : (
            <p className="text-center">
              Drag 'n' drop {multiple ? "files" : "a file"} here,
              <br /> or click to select
            </p>
          )}
        </div>
      </div>

      <div className={cn("gap-4 mt-4", multiple && "grid grid-cols-3")}>
        {uploadedFiles.map((uploadedFile, i) => (
          <FileItem
            key={`${uploadedFile.file.name}-${i}`}
            uploadedFile={uploadedFile}
            multiple={multiple}
            allowCopy={allowCopy}
            onRemove={handleRemoveUploadedFile}
            onRetry={handleRetry}
            onNameChange={handleNameChange}
          />
        ))}
      </div>

      {manualUpload &&
        uploadedFiles.some((uf) => !uf.id && !uf.uploading) && (
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={uploadWithNames}
              className="px-4 py-2 bg-[#1daa7e] text-white rounded-md hover:bg-[#158f6a] transition-colors"
            >
              Upload All with Names
            </button>
          </div>
        )}
    </>
  );
}

export default FileUpload;
